import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, TextField, MenuItem, CircularProgress, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton
} from '@mui/material';
import { ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { getDisciplineOffers, getDofStudents, updateStudentOrder } from '../../../store/actions/disciplineOfferActions';
import { RootState } from '../../../store/reducers';

const DisciplineOfferStudentOrder = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const disciplineOffers = useSelector((state: RootState) => state.disciplineOffers.disciplineOffers);
    const dofStudents = useSelector((state: RootState) => state.disciplineOffers.dofStudents);
    const isFetching = useSelector((state: RootState) => state.disciplineOffers.isFetching);
    const isFetchingStudents = useSelector((state: RootState) => state.disciplineOffers.isFetchingStudents);

    const [selectedDofId, setSelectedDofId] = useState<number | ''>('');
    const [orderedStudents, setOrderedStudents] = useState<any[]>([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        dispatch(getDisciplineOffers());
    }, [dispatch]);

    useEffect(() => {
        if (selectedDofId) {
            dispatch(getDofStudents(selectedDofId as number));
        }
    }, [dispatch, selectedDofId]);

    useEffect(() => {
        if (dofStudents && Array.isArray(dofStudents)) {
            setOrderedStudents(dofStudents.map((s: any, index: number) => ({
                ...s,
                order: s.order ?? index + 1
            })));
            setHasChanges(false);
        }
    }, [dofStudents]);

    const handleDofChange = (dofId: number) => {
        setSelectedDofId(dofId);
        setHasChanges(false);
    };

    const moveStudent = (index: number, direction: 'up' | 'down') => {
        const newList = [...orderedStudents];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newList.length) return;

        // Swap items
        const temp = newList[index];
        newList[index] = newList[targetIndex];
        newList[targetIndex] = temp;

        // Update order numbers
        const reordered = newList.map((s: any, i: number) => ({ ...s, order: i + 1 }));
        setOrderedStudents(reordered);
        setHasChanges(true);
    };

    const handleOrderChange = (index: number, newOrder: number) => {
        const newList = [...orderedStudents];
        newList[index] = { ...newList[index], order: newOrder };
        setOrderedStudents(newList);
        setHasChanges(true);
    };

    const handleSave = () => {
        if (!selectedDofId) return;
        const orderData = orderedStudents.map((s: any) => ({
            id: s.id,
            order: s.order
        }));
        dispatch(updateStudentOrder(selectedDofId as number, orderData));
        setHasChanges(false);
        enqueueSnackbar('Ordem salva com sucesso!', { variant: 'success' });
    };

    const getDofLabel = (dof: any) => {
        const discipline = dof?.disciplineModule?.discipline?.Description || '';
        const module = dof?.disciplineModule?.Description || '';
        const classOffer = dof?.classOffer?.Description || '';
        return `${discipline} - ${module} (${classOffer})`;
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Ordem de Alunos na Oferta</Typography>
                {hasChanges && (
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        color="primary"
                    >
                        Salvar Ordem
                    </Button>
                )}
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <TextField
                    label="Selecionar Oferta de Disciplina"
                    select
                    value={selectedDofId}
                    onChange={(e) => handleDofChange(Number(e.target.value))}
                    fullWidth
                    sx={{ mb: 2 }}
                >
                    <MenuItem value="">
                        <em>Selecione uma oferta...</em>
                    </MenuItem>
                    {(disciplineOffers || []).map((dof: any) => (
                        <MenuItem key={dof.id} value={dof.id}>
                            {getDofLabel(dof)}
                        </MenuItem>
                    ))}
                </TextField>
            </Paper>

            {isFetching || isFetchingStudents ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : selectedDofId && orderedStudents.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={80}>Ordem</TableCell>
                                <TableCell>Nome do Aluno</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell width={120} align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orderedStudents.map((student: any, index: number) => (
                                <TableRow key={student.id || index} hover>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={student.order}
                                            onChange={(e) => handleOrderChange(index, parseInt(e.target.value, 10) || 0)}
                                            sx={{ width: 60 }}
                                            inputProps={{ min: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell>{student.Name || student.name || ''}</TableCell>
                                    <TableCell>{student.email || ''}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => moveStudent(index, 'up')}
                                            disabled={index === 0}
                                        >
                                            <ArrowUpIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => moveStudent(index, 'down')}
                                            disabled={index === orderedStudents.length - 1}
                                        >
                                            <ArrowDownIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : selectedDofId ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Nenhum aluno encontrado nesta oferta de disciplina.
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Selecione uma oferta de disciplina para visualizar os alunos.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default DisciplineOfferStudentOrder;
